import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapMarker, MapView, tangerRasterSource, useMap, MAP_MIN_ZOOM, type MapViewport } from '@wehunt/map'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import { TeamMapLayers } from '../TeamMapLayers'
import { useHuntMapAppearance } from '../../lib/useHuntMapAppearance'
import { useBaseMapTileInfo } from '../../lib/useBaseMapTileInfo'
import { groundFramePoints, pointsToBBox } from '../../lib/mapSeedView'
import type { GeoArea, GamePinType, GamePinView, GeoPt, TeamMap } from '../../lib/types'
import { ROW_TYPE_LABEL_KEY } from './gameReports'

/**
 * Where the hunt's game was recorded, on real terrain.
 *
 * Built on `@wehunt/map` — the same MapLibre engine every other read/write map
 * surface in this package runs on — rather than the hand-rolled Web-Mercator
 * projection the prototype's `ReportMap` used: panning, zooming, and placing
 * tiles and markers are the map engine's job, not this component's.
 *
 * Draws the hunting ground under the game pins, so a report shows where the hunt
 * happened rather than a handful of markers on bare terrain.
 *
 * Tracks and dog trails are deliberately absent. The prototype's map draws them
 * because its mock data carries them; in production they live in the replay
 * archive, and reading a multi-megabyte archive to paint a still image would give
 * this page the replay's fragile dependency for something the "Replay hunt" button
 * in the header already does better.
 */
export interface ReportMapProps {
  gamePins: GamePinView[]
  /**
   * The team's ground, drawn under the pins. Optional because a report can be
   * opened before the team's grounds have loaded, and a report without a ground
   * is still worth reading.
   */
  boundaryAreas?: readonly GeoArea[]
  subAreaPolygons?: readonly GeoArea[]
  forbiddenPolygons?: readonly GeoArea[]
  teamBorderColor?: string
  height: number | string
  /** The pin the map centres its label on, if any. */
  selectedPinId?: string | null
  onSelectPin?: (pinId: string | null) => void
  /**
   * Whether selecting a pin pans/zooms the camera to it. Off by default, so the
   * small preview map and the full-screen modal only grow-and-label the selected
   * pin without moving. Only the report page's big top map opts in, so a row
   * click navigates that one map to the game while the others hold their frame.
   */
  recenterOnSelect?: boolean
  /**
   * Placement mode. When set, the map hosts a centre-pin location picker: this
   * game-type's pin art sits FIXED at the map's visual centre, and the map's
   * centre coordinate is streamed back through `onDraftCenterChange` as the user
   * pans. Null (the default) is the normal report view. Driven by the add/edit
   * form on the report page — the form lives beside this map, not on it.
   */
  draftPinType?: GamePinType | null
  /** Where the map opens when placement mode begins — the edited pin's location,
   *  else the ground centroid, else a wide fallback (see `resolvePinPickerView`). */
  draftInitialView?: MapViewport | null
  /** Streams the map's centre while in placement mode, so the form can submit it. */
  onDraftCenterChange?: (center: GeoPt) => void
}

const MIN_SPAN_DEG = 0.004

// `MapView` reads `minZoom`/`maxZoom` once, at construction (its mount effect
// has `[]` deps) — well before `useBaseMapTileInfo`'s async `/api/maps` fetch
// can possibly have answered. Deriving these from `tileInfo` therefore can
// never take effect: the map would be permanently on whatever `tileInfo` was
// (always null) on the render that mounted it. Fixed constants say that
// honestly instead of pretending a live derivation. The catalogue's real
// bounds still govern tile fetching — they live on the raster SOURCE via
// `tangerRasterSource`'s `minzoom`/`maxzoom`, which `MapView` re-applies on
// every `tileInfo` change; beyond the source's `maxzoom`, MapLibre overzooms
// by scaling up the deepest available parent tile rather than fetching one
// that doesn't exist.
const REPORT_MIN_ZOOM = 1
const REPORT_MAX_ZOOM = 19

interface GeoBounds {
  minLat: number
  maxLat: number
  minLon: number
  maxLon: number
}

function clampLat(lat: number): number {
  return Math.max(-85, Math.min(85, lat))
}

function clampLon(lon: number): number {
  return Math.max(-180, Math.min(180, lon))
}

/** The extent of the pins, widened so a single pin is not framed at max zoom. */
function boundsOf(pins: GamePinView[]): GeoBounds | null {
  if (pins.length === 0) return null
  let minLat = Infinity
  let maxLat = -Infinity
  let minLon = Infinity
  let maxLon = -Infinity
  for (const pin of pins) {
    minLat = Math.min(minLat, pin.location.lat)
    maxLat = Math.max(maxLat, pin.location.lat)
    minLon = Math.min(minLon, pin.location.lon)
    maxLon = Math.max(maxLon, pin.location.lon)
  }
  // One pin, or several in the same spot, gives a zero-span box. fitBounds would
  // then solve for the deepest zoom the tiles have, which reads as a random patch
  // of forest — pad it into a neighbourhood instead.
  const padLat = Math.max(0, (MIN_SPAN_DEG - (maxLat - minLat)) / 2)
  const padLon = Math.max(0, (MIN_SPAN_DEG - (maxLon - minLon)) / 2)
  return {
    minLat: clampLat(minLat - padLat),
    maxLat: clampLat(maxLat + padLat),
    minLon: clampLon(minLon - padLon),
    maxLon: clampLon(maxLon + padLon),
  }
}

/** The zoom stack, rendered inside `MapView`'s children so `useMap()` resolves
 *  to the live map instance. */
function ZoomControls({ zoomInTitle, zoomOutTitle }: { zoomInTitle: string; zoomOutTitle: string }) {
  const map = useMap()
  return (
    <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <button type="button" className="wh-btn-icon" onClick={() => map?.zoomIn()} title={zoomInTitle}>
        <Icon name="zoom-in" size={18} />
      </button>
      <button type="button" className="wh-btn-icon" onClick={() => map?.zoomOut()} title={zoomOutTitle}>
        <Icon name="zoom-out" size={18} />
      </button>
    </div>
  )
}

/** How long the camera glides to a picked pin — long enough to read as a move
 *  on the same map, short enough not to feel sluggish. */
const RECENTER_MS = 450

/**
 * Pans (and gently zooms in on) the live map when the SELECTED pin changes, so
 * clicking a report row — or the pin button — navigates the map to that game.
 * Rendered inside `MapView`'s children so `useMap()` resolves to the map
 * instance, the same pattern `ZoomControls` uses.
 *
 * Stays mounted across placement mode (gated by `active`) rather than being
 * unmounted and remounted: a remount's fresh effect would re-pan to the still-
 * selected pin the moment the add/edit form closes, jerking the camera away from
 * where the user left it. It tracks the last pin it panned to, so it fires once
 * per genuine selection change and never merely because `active` toggled.
 */
function RecenterOnSelected({
  active,
  pinId,
  lat,
  lon,
}: {
  active: boolean
  pinId: string | null
  lat: number | null
  lon: number | null
}) {
  const map = useMap()
  const lastPannedTo = useRef<string | null>(null)
  useEffect(() => {
    // Inert while placing — the camera is the user's to pan.
    if (!map || !active) return
    if (pinId === null) {
      lastPannedTo.current = null
      return
    }
    // Only a real change of selection pans; re-entering the effect with the same
    // pin (e.g. because placement mode just ended) must leave the camera alone.
    if (pinId === lastPannedTo.current) return
    lastPannedTo.current = pinId
    if (lat === null || lon === null) return
    map.easeTo({ center: [lon, lat], zoom: Math.max(map.getZoom(), RECENTER_ZOOM), duration: RECENTER_MS })
  }, [map, active, pinId, lat, lon])
  return null
}

/**
 * Seeds the camera to the picker's opening view ONCE, on the transition INTO
 * placement mode, for a map that was already mounted showing pins. It never
 * moves the camera on the way OUT: leaving placement mode keeps exactly the frame
 * the user left, so pressing Done doesn't jerk the map. The very first mount is
 * skipped too — there the map's own `initialView`/`initialBounds` positioned it.
 */
function DraftCameraSeed({ active, view }: { active: boolean; view: MapViewport | null }) {
  const map = useMap()
  const wasActive = useRef<boolean | null>(null)
  useEffect(() => {
    if (!map) return
    const prev = wasActive.current
    wasActive.current = active
    // First run (mount): the map is already positioned by its initial camera.
    if (prev === null) return
    // Seed only on false → true (entering placement mode), never on true → false.
    if (active && !prev && view) {
      map.jumpTo({ center: [view.center.lon, view.center.lat], zoom: view.zoom })
    }
  }, [map, active, view])
  return null
}

/** The map zooms IN to at least this level on a pin, but never zooms out — a
 *  reader who has zoomed in to inspect keeps their closer view. */
const RECENTER_ZOOM = 14

export function ReportMap({
  gamePins,
  height,
  selectedPinId = null,
  onSelectPin,
  recenterOnSelect = false,
  boundaryAreas,
  subAreaPolygons,
  forbiddenPolygons,
  teamBorderColor,
  draftPinType = null,
  draftInitialView = null,
  onDraftCenterChange,
}: ReportMapProps) {
  const huntMapAppearance = useHuntMapAppearance()
  const { t } = useTranslation('huntInvitation')
  const tileInfo = useBaseMapTileInfo()

  // Placement mode: the form beside this map is open, and this map is its picker.
  const draftActive = draftPinType != null

  const bounds = useMemo(() => boundsOf(gamePins), [gamePins])
  const initialBounds = useMemo<[number, number, number, number] | null>(() => {
    if (bounds) return [bounds.minLon, bounds.minLat, bounds.maxLon, bounds.maxLat]
    // No pins to frame on — fall back to the hunting ground so a no-game report
    // opens on the ground rather than a regional default. Computed only on this
    // branch, so a report that HAS pins never walks the ground's vertices.
    return pointsToBBox(groundFramePoints(boundaryAreas ?? []))
  }, [bounds, boundaryAreas])

  const baseSource = useMemo(
    () => (tileInfo ? tangerRasterSource({ ...tileInfo, widths: [] }) : null),
    [tileInfo],
  )

  // Only feeds TeamMapLayers' zoom-gated details, which is moot here —
  // `subAreaLabels={false}` suppresses the one thing that reads it — so a
  // placeholder value before the map's first view report costs nothing.
  const [liveZoom, setLiveZoom] = useState(MAP_MIN_ZOOM)
  // The draft callback and pin type are read through refs so `handleViewChange`
  // stays stable (MapView captures it once): every `moveend` in placement mode
  // reports the new centre back to the form, and the mount report seeds it.
  const draftActiveRef = useRef(draftActive)
  draftActiveRef.current = draftActive
  const onDraftCenterChangeRef = useRef(onDraftCenterChange)
  onDraftCenterChangeRef.current = onDraftCenterChange
  const handleViewChange = useCallback((v: MapViewport) => {
    setLiveZoom(v.zoom)
    if (draftActiveRef.current) onDraftCenterChangeRef.current?.({ lat: v.center.lat, lon: v.center.lon })
  }, [])

  const reportGroundMap = useMemo<TeamMap>(
    () => ({
      name: '',
      updated: '',
      boundaryAreas: [...(boundaryAreas ?? [])],
      subAreaPolygons: subAreaPolygons ? [...subAreaPolygons] : undefined,
      forbiddenPolygons: forbiddenPolygons ? [...forbiddenPolygons] : undefined,
      borderColor: teamBorderColor,
      objects: [],
      lines: [],
    }),
    [boundaryAreas, subAreaPolygons, forbiddenPolygons, teamBorderColor],
  )

  const selected = gamePins.find((p) => p.id === selectedPinId) ?? null

  const containerStyle = {
    position: 'relative' as const,
    height,
    width: '100%',
    overflow: 'hidden' as const,
    borderRadius: 10,
    background: COLORS.surfaceContainerHighest,
  }

  // A pin-less report still shows the hunting ground — a reader wants to see
  // WHERE the hunt was even when no game was recorded, and the empty grey box it
  // used to show read as broken. A small caption below keeps the "no game" state
  // explicit without hiding the terrain.
  const noGame = gamePins.length === 0 && !draftActive

  // Nothing to frame at all — no game pins AND no ground boundary (initialBounds
  // is the ground's extent on this branch, since there are no pins). Keep the
  // plain message rather than opening bare terrain at a regional default. (A
  // report WITH a ground but no game renders the map below, framed on the ground.)
  if (noGame && initialBounds === null) {
    return (
      <div role="img" aria-label={t('report.mapLabel')} style={containerStyle}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            color: COLORS.outline,
            textAlign: 'center',
            padding: 20,
          }}
        >
          {t('report.mapNoPins')}
        </div>
      </div>
    )
  }

  return (
    <div role="img" aria-label={t('report.mapLabel')} style={containerStyle}>
      <MapView
        // Deliberately NOT keyed on placement mode. A remount on exit would refit
        // the map to the pins (or re-seed), jerking the camera away from where the
        // user left it the instant they press Done. The map mounts once; entering
        // placement mode seeds it imperatively via DraftCameraSeed, and leaving it
        // touches the camera not at all. `initialBounds`/`initialView` are read
        // only at that first mount: null bounds + the draft view when the map's
        // very first render is already in placement mode (a pin-less report placing
        // its first pin), the pins' fit otherwise.
        baseSource={baseSource}
        initialBounds={draftActive ? null : initialBounds}
        initialView={draftActive ? (draftInitialView ?? undefined) : undefined}
        interactive
        minZoom={REPORT_MIN_ZOOM}
        maxZoom={REPORT_MAX_ZOOM}
        onViewChange={handleViewChange}
      >
        <TeamMapLayers
          map={reportGroundMap}
          appearance={huntMapAppearance}
          zoom={liveZoom}
          subAreaLabels={false}
        />

        {gamePins.map((pin) => {
          const isSelected = pin.id === selectedPinId
          return (
            <MapMarker key={pin.id} lat={pin.location.lat} lon={pin.location.lon} anchor="bottom">
              <button
                type="button"
                onClick={() => onSelectPin?.(isSelected ? null : pin.id)}
                aria-pressed={isSelected}
                aria-label={pin.games?.[0]?.name ?? t('report.mapPinLabel')}
                style={{
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  lineHeight: 0,
                  filter: isSelected ? 'drop-shadow(0 0 4px rgba(0,0,0,0.55))' : undefined,
                  transform: isSelected ? 'scale(1.15)' : undefined,
                  // The pin's tip marks the spot, so a selected pin must grow
                  // from its tip rather than its centre.
                  transformOrigin: '50% 100%',
                }}
              >
                <img
                  // Every pin type has its own shipped art; an observed sighting
                  // must not borrow the wounded pin, which would read as an
                  // outstanding tracking job rather than a report of game seen.
                  src={`/assets/map_pin_${pin.pinType}.svg`}
                  alt=""
                  width={26}
                  height={27}
                  style={{ display: 'block' }}
                />
              </button>
            </MapMarker>
          )
        })}

        <ZoomControls zoomInTitle={t('report.zoomIn')} zoomOutTitle={t('report.zoomOut')} />

        {/* Recentre only when the host opted in (the big top map): the preview and
            modal leave `recenterOnSelect` false, so they highlight without moving.
            Kept mounted across placement mode and gated by `active` (rather than
            unmounted) so ending placement mode doesn't re-pan to the still-selected
            pin — see RecenterOnSelected. */}
        {recenterOnSelect ? (
          <RecenterOnSelected
            active={!draftActive}
            pinId={selectedPinId}
            lat={selected?.location.lat ?? null}
            lon={selected?.location.lon ?? null}
          />
        ) : null}

        {/* Seeds the camera to the picker view when placement mode BEGINS on an
            already-mounted map (e.g. editing a pin centres it). Never moves the
            camera when placement mode ends — that's the whole point. */}
        <DraftCameraSeed active={draftActive} view={draftInitialView} />
      </MapView>

      {/* Placement mode: the game-type pin sits FIXED at the map's visual centre
          (its tip on centre via translate -50%/-100%), and the map's centre
          coordinate is where the pin lands. `pointerEvents:none` lets a drag fall
          through to the map. Same shipped art the markers and counts pill use. */}
      {draftActive ? (
        <div
          data-testid="report-map-draft-pin"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            lineHeight: 0,
            zIndex: 1,
          }}
        >
          <img
            src={`/assets/map_pin_${draftPinType}.svg`}
            alt=""
            width={34}
            height={35}
            style={{ display: 'block', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.45))' }}
          />
        </div>
      ) : null}

      {!draftActive && selected !== null ? (
        <div
          style={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            maxWidth: 'calc(100% - 24px)',
            background: COLORS.surfaceContainerLowest,
            border: `1px solid ${COLORS.surfaceContainerHighest}`,
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600 }}>
            {selected.games?.map((g) => g.name).join(', ') || t('report.mapPinLabel')}
          </div>
          <div style={{ fontSize: 12, color: COLORS.outline }}>
            {t(ROW_TYPE_LABEL_KEY[selected.pinType])}
          </div>
        </div>
      ) : null}

      {/* No game recorded: keep the ground visible, but say so, unobtrusively.
          Anchored TOP-centre (the bottom edge carries the selected-pin card and,
          in GameReportsCard, a "View all on map" button) and allowed to wrap, so a
          longer localized string stays legible instead of clipping on a narrow
          preview. `pointerEvents:none` so it never blocks panning underneath. */}
      {noGame ? (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 12,
            transform: 'translateX(-50%)',
            maxWidth: 'calc(100% - 24px)',
            background: COLORS.surfaceContainerLowest,
            border: `1px solid ${COLORS.surfaceContainerHighest}`,
            borderRadius: 8,
            padding: '6px 10px',
            fontSize: 13,
            color: COLORS.outline,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          {t('report.mapNoPins')}
        </div>
      ) : null}
    </div>
  )
}
